import { v4 as uuid } from 'uuid'
import {
  amplitude,
  identifyAmplitudeUser,
  initAmplitude,
  updateAmplitudeUserProperties,
} from './platforms/amplitude'
import {
  Pixel as facebookPixel,
  updateFacebookAdvancedMatching,
} from './platforms/facebook'
import {
  serverSideAnalytics,
  ServerSideAnalyticsPlatform,
} from './ServerSideAnalytics'
import { ALL_UTM_PARAMS } from './platforms/utm'
import { intercomService } from './platforms/intercom'
import { initGoogleAnalytics } from './platforms/gtm'
import { setUserPhoneNumber } from '../User/phoneNumber'
import { getCleanUrlParam, getUrlParam } from '../../utils/UrlUtils'
import { mixtilesAxios } from '../../utils/ApiUtils'
import storageManager from '../StorageManager'
import { getSha256 } from '../../utils/cryptoUtils'
import SerialTaskRunner from '../SerialTaskRunner'
import { experimentManager } from '../ExperimentManager/ExperimentManager'
import { faceBookBlacklistEvents } from './platforms/faceBookBlackListEvents'
import { getBrazeInstance } from './platforms/braze.client'
import { brazeWhitelistEvents } from './platforms/brazeWhitelistEvents'
import { toBrazeExternalId } from './platforms/braze.utils'
import {
  setDefaultEmailSubscriptionByCountry,
  handleBrazeCustomAttributeArrays,
  setBrazeUserProperties,
} from './platforms/braze'
import { logger } from '../logger'
import { userState } from '../User/userState'
import { runAsyncWithRetries } from '../../utils/promises'
import {
  BRAZE_PROMO_CODE_CONSOLIDATION,
  PRELOAD_URL_PARAM,
  LAST_ATTRIBUTION_PARAMS_TIME_KEY,
  LOCAL_STORAGE_KEY,
  USER_PHONE_NUMBER_KEY,
  USER_COUNTRY_CODE_KEY,
} from './Analytics.consts'
import { redactSecretsInEventProperties } from 'utils/logRedaction'

function getBrazeConsolidatedEventName(eventName) {
  for (const [consolidatedName, originalEvents] of Object.entries(
    BRAZE_PROMO_CODE_CONSOLIDATION
  )) {
    if (originalEvents.includes(eventName)) {
      return consolidatedName
    }
  }
  return eventName
}

const SCREEN_VIEW_URL_PARAMS = [
  ...ALL_UTM_PARAMS,
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
  'utm_id',
]

function getReferringDomain() {
  if (!document.referrer) return undefined
  try {
    return new URL(document.referrer).hostname
  } catch {
    return undefined
  }
}

function getScreenViewUrlParams() {
  const params = {}
  for (const key of SCREEN_VIEW_URL_PARAMS) {
    params[key] = getUrlParam(key)
  }
  return {
    '[Amplitude] Page Domain': window.location.hostname,
    '[Amplitude] Page Location': window.location.href,
    '[Amplitude] Page Path': window.location.pathname,
    '[Amplitude] Page Title': document.title,
    '[Amplitude] Page URL': window.location.href,
    referrer: document.referrer || undefined,
    referring_domain: getReferringDomain(),
    ...params,
  }
}

export class Analytics {
  identified = false

  screenViewCounter = 0

  _trackOncePerSessionEvents = new Set()

  facebookPixelAlreadyTrackedInSesson = [] // this array reflect only one-time *session* (session mean that after the user exit the app the session restarts) events and not all time (like this.alreadyTracked)

  _userCountryCode = null

  isAmplitudeReady = false

  amplitudeTaskRunner = new SerialTaskRunner()

  pendingMetaCalls = new Map()

  async init(serverDeviceId) {
    this.userId = null
    this.braze = getBrazeInstance()
    intercomService.init()
    initGoogleAnalytics()
    window.addEventListener('pagehide', () => this.runPendingMetaCalls())
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.runPendingMetaCalls()
      }
    })
    await initAmplitude(serverDeviceId)
    this.manageUtmParams()
  }

  async getEmailFromBrazeId(brazeId) {
    try {
      const { data } = await mixtilesAxios.get(
        `v4/user/emailFromBrazeId/${brazeId}`
      )
      logger.info('Email fetched from brazeId', { data, brazeId })
      return data.email
    } catch (error) {
      logger.error('Failed to fetch email from brazeId', {
        brazeId,
        error: error.message,
      })
      throw error
    }
  }

  syncStorage() {
    const alreadyTracked = Array.from(this.alreadyTracked)
    const alreadyTrackedPerId = Array.from(this.alreadyTrackedPerId)
    storageManager.set(LOCAL_STORAGE_KEY, {
      alreadyTracked,
      alreadyTrackedPerId,
    })
  }

  manageUtmParams() {
    const utmParams = this.getUTMParams()
    const setParams = { ...utmParams, 'App Platform': this.getAppPlatform() }
    const unsetParams = []
    if (Object.keys(utmParams).length > 0) {
      storageManager.set(LAST_ATTRIBUTION_PARAMS_TIME_KEY, Date.now())
    } else {
      const lastAttributionParamsTime = storageManager.get(
        LAST_ATTRIBUTION_PARAMS_TIME_KEY
      )
      if (lastAttributionParamsTime) {
        const timePassed = Date.now() - lastAttributionParamsTime
        if (timePassed > 1000 * 60 * 60 * 24) {
          storageManager.remove(LAST_ATTRIBUTION_PARAMS_TIME_KEY)
          unsetParams.push(...ALL_UTM_PARAMS)
        }
      } else {
        unsetParams.push(...ALL_UTM_PARAMS)
      }
    }

    updateAmplitudeUserProperties({
      set: setParams,
      unset: unsetParams,
    }).then(() => {
      this.isAmplitudeReady = true
      this.amplitudeTaskRunner.runTasks()
    })
  }

  async identifyUser(userId, email = null, emailConsent = null) {
    // This makes sure we store amplitude's generic params on the correct userId.
    // This fixes discrepancies that happen when the localStorage is empty but the amplitude cookie exists.
    // The facebook browser, for example, empties the localStorage sometimes (but keeps the cookies).
    // In these cases we will create a new userId, but Amplitude will get the id from the cookie.
    // This will only affect generic params, since they are collected before we call amplitude.setUserId

    let brazeExternalId = null

    if (email) {
      // Use SHA-256 hash of lowercased email as Braze external_id when email is available
      brazeExternalId = toBrazeExternalId(email)
      this.braze.changeUser(brazeExternalId)
    } else {
      const user = this.braze.getUser()
      if (!user) {
        logger.warning('Braze user not available for addAlias', { userId })
      }
      user.addAlias(userId, 'anonymous user id')
    }

    // Set default email subscription based on country (fire-and-forget with retry)
    runAsyncWithRetries({
      op: () =>
        setDefaultEmailSubscriptionByCountry(
          this.braze,
          email,
          brazeExternalId,
          userId,
          emailConsent
        ),
      opName: 'setDefaultEmailSubscriptionByCountry',
      numRetries: 3,
      minTimeout: 1100, // 100ms delay between retries to give Braze time to sync
      logMetadata: {
        email: !!email,
        brazeExternalId: !!brazeExternalId,
        emailConsent,
      },
    }).catch((error) => {
      // Final failure after all retries - just log, don't throw (fire-and-forget)
      logger.error(
        'setDefaultEmailSubscriptionByCountry failed after all retries',
        {
          error: error.message,
          email: !!email,
          brazeExternalId: !!brazeExternalId,
          emailConsent,
        }
      )
    })

    window.dataLayer.push({
      userId,
    })
    // Use username, which is per device, instead of userId (unifiedUid) to prevent conversation leakage when users enter unverified emails during onboarding
    const username = userState.getUser()?.username
    if (username) {
      intercomService.identifyUser(username)
    }
    identifyAmplitudeUser(userId)
    this.userId = userId
  }

  // Meta's pixel does tens of ms of sync work per call, so each runs in its own timer task (FIFO),
  // out of the interaction that tracked it. A hidden page has nothing to paint and may be frozen or
  // unloaded before timers fire, so calls run immediately while hidden and pending ones run on hide.
  deferMetaCall(call) {
    if (document.visibilityState === 'hidden') {
      this.runMetaCall(call)
      return
    }
    const timeoutId = setTimeout(() => {
      this.pendingMetaCalls.delete(timeoutId)
      this.runMetaCall(call)
    })
    this.pendingMetaCalls.set(timeoutId, call)
  }

  runPendingMetaCalls() {
    for (const [timeoutId, call] of this.pendingMetaCalls) {
      clearTimeout(timeoutId)
      this.pendingMetaCalls.delete(timeoutId)
      this.runMetaCall(call)
    }
  }

  runMetaCall(call) {
    try {
      call()
    } catch (error) {
      logger.error('Analytics track error', error)
    }
  }

  getAppPlatform() {
    // since pwa installed from play store has platform: web, we want to distinguish between web and android
    // This pwa host is used only on play store so I use it for now
    if (typeof window !== 'undefined' && window.location.host.includes('pwa')) {
      return 'Android'
    } else {
      return 'Web'
    }
  }

  getUTMParams() {
    const parsedUtmParams = {}
    for (const utmParam of ALL_UTM_PARAMS) {
      const utmValue = getUrlParam(utmParam)
      if (utmValue) parsedUtmParams[utmParam] = utmValue
    }
    return parsedUtmParams
  }

  async setUserEmail({
    email,
    isNewEmail,
    trackEventName,
    trackProperties = {},
  }) {
    const hashEmail = email ? getSha256(email) : null
    const emailProperties = {
      Email: email,
      'Hash Email': hashEmail,
      'Is New Email': isNewEmail,
      ...trackProperties,
    }
    this.setUserProperties(emailProperties)

    // Get Braze user once for all operations in this function
    const brazeUser = this.braze.getUser()
    if (!brazeUser) {
      logger.warning('Braze user not available for setUserEmail operations', {
        email: !!email,
      })
    }

    brazeUser.setEmail(email)
    // Set Unified UID as user property if available (will update Braze custom attribute)
    if (this.userId) {
      this.setUserProperties({
        'Unified UID': this.userId,
      })
    }

    const userProperties = experimentManager.getUserProperties()
    const { Email, ...customProps } = userProperties

    setBrazeUserProperties(this.braze, customProps)

    intercomService.setEmail(email)
    intercomService.setActiveExperiments(userProperties)
    this.track(trackEventName, emailProperties, {
      serverSideAnalyticsPlatforms: [
        ServerSideAnalyticsPlatform.Facebook,
        ServerSideAnalyticsPlatform.Braze,
      ],
    })
    window.dataLayer.push({ userEmail: email })
    this.deferMetaCall(() => updateFacebookAdvancedMatching({ email }))
  }

  syncPhoneToAdNetworks(number) {
    setUserPhoneNumber(number)
    this.deferMetaCall(() =>
      updateFacebookAdvancedMatching({ phoneNumber: number })
    )
  }

  setPhoneNumber(number) {
    const user = this.braze.getUser()
    if (!user) {
      logger.warning('Braze user not available for setPhoneNumber', {
        number: !!number,
      })
    }
    user.setPhoneNumber(number)
    this.syncPhoneToAdNetworks(number)
    window.dataLayer.push({ [USER_PHONE_NUMBER_KEY]: number })
  }

  setUserCountryCode(countryCode) {
    this._userCountryCode = countryCode
  }

  async setUserProperties(userProperties) {
    const promise = updateAmplitudeUserProperties({ set: userProperties })
    intercomService.setUserProperties(userProperties)

    setBrazeUserProperties(this.braze, userProperties)

    await promise
  }

  track(
    eventName,
    rawEventProperties = {},
    { serverSideAnalyticsPlatforms = [], eventID = uuid() } = {}
  ) {
    const eventProperties = redactSecretsInEventProperties(rawEventProperties)
    const propertiesWithCountry = this.addUserCountryCode(eventProperties)
    try {
      if (window.KEYS.runtimeEnv !== 'production' && !window.KEYS.silent) {
        // eslint-disable-next-line no-console
        console.log(`[Event] ${eventName} - ${JSON.stringify(eventProperties)}`)
      }

      if (this.isAmplitudeReady) {
        amplitude.track(eventName, eventProperties)
      } else {
        this.amplitudeTaskRunner.addTask(async () => {
          amplitude.track(eventName, eventProperties)
        })
      }
      this.reportServerAnalytics(
        eventName,
        eventProperties,
        eventID,
        serverSideAnalyticsPlatforms
      )
      if (!faceBookBlacklistEvents.includes(eventName)) {
        // Raw fbq call: the wrapper's trackCustom drops the eventID Meta needs to dedupe server events
        this.deferMetaCall(() =>
          facebookPixel.fbq('trackCustom', eventName, propertiesWithCountry, {
            eventID,
          })
        )
      }
      intercomService.track(eventName, eventProperties)

      // Transform event name for Braze, then check if it should be sent
      const brazeEventName = getBrazeConsolidatedEventName(eventName)
      if (brazeWhitelistEvents.includes(brazeEventName)) {
        handleBrazeCustomAttributeArrays(
          this.braze,
          brazeEventName,
          eventProperties,
          eventID,
          eventName,
          this.getAppPlatform()
        )
      }

      window.dataLayer.push({
        event: eventName,
        props: propertiesWithCountry,
        eventID,
      })
    } catch (error) {
      logger.error('Analytics track error', error)
    }
  }

  addUtmParamsIfMissing(eventProperties) {
    try {
      const propsKeys = Object.keys(eventProperties)
      let props = eventProperties

      if (!propsKeys.some((k) => k.startsWith('utm_'))) {
        const utmParams = this.getUTMParams()
        if (Object.values(utmParams).some((v) => v !== undefined)) {
          props = { ...props, ...utmParams }
        }
      }

      return props
    } catch (error) {
      logger.error('Analytics addUtmParamsIfMissing error', error)
      return eventProperties
    }
  }

  addUserCountryCode(eventProperties = {}) {
    return this._userCountryCode
      ? { ...eventProperties, [USER_COUNTRY_CODE_KEY]: this._userCountryCode }
      : eventProperties
  }

  reportServerAnalytics(
    eventName,
    eventProperties,
    eventID,
    serverSideAnalyticsPlatforms
  ) {
    const props = this.addUtmParamsIfMissing(
      redactSecretsInEventProperties(eventProperties)
    )
    serverSideAnalytics.track(
      eventName,
      props,
      eventID,
      serverSideAnalyticsPlatforms
    )
  }

  trackOnce(eventName, eventProperties) {
    if (!this.alreadyTracked) {
      this.initAlreadyTracked()
    }

    if (this.wasTracked(eventName)) {
      return
    }
    this.track(eventName, eventProperties)
    this.setAsTracked(eventName)
  }

  trackOncePerId(eventName, id, eventProperties) {
    if (!this.alreadyTrackedPerId) {
      this.initAlreadyTracked()
    }

    if (this.wasTrackedPerId(eventName, id)) {
      return
    }
    this.track(eventName, eventProperties)
    this.setAsTrackedPerId(eventName, id)
  }

  trackScreenView(screenName, eventProperties = {}) {
    if (getUrlParam(PRELOAD_URL_PARAM)) {
      return
    }
    this.screenViewCounter++
    const navigationType =
      this.screenViewCounter === 1
        ? performance?.getEntriesByType?.('navigation')?.[0]?.type ?? 'unknown'
        : 'client_navigation'
    this.track('Screen View', {
      'Screen Name': screenName,
      Path: window.location.pathname,
      ...getScreenViewUrlParams(),
      '[Amplitude] Page Counter': this.screenViewCounter,
      'Navigation Type': navigationType,
      ...eventProperties,
    })
  }

  facebookPixelTrackOnceInSession(eventName, data = {}) {
    if (!this.facebookPixelAlreadyTrackedInSesson.includes(eventName)) {
      this.facebookPixelAlreadyTrackedInSesson.push(eventName)
      this.facebookTrack(eventName, data)
    }
  }

  facebookTrack(eventName, data = {}, eventID = uuid()) {
    const properties = this.addUserCountryCode(
      redactSecretsInEventProperties(data)
    )
    // Raw fbq call: the wrapper's track drops the eventID Meta needs to dedupe server events
    this.deferMetaCall(() =>
      facebookPixel.fbq('track', eventName, properties, { eventID })
    )
    this.reportServerAnalytics(eventName, properties, eventID, [
      ServerSideAnalyticsPlatform.Facebook,
    ])
  }

  // This method tracks regular client side analytics but only tracks once per session to the server.
  // This is useful for high-load, frequent actions, like "Photo Picked"
  trackWithOneTimeServerSide(
    eventName,
    eventProperties,
    serverSideAnalyticsPlatforms = []
  ) {
    const eventID = uuid()
    const isFirstInSession = !this._trackOncePerSessionEvents.has(eventName)
    const properties = {
      ...eventProperties,
      'Is First In Session': isFirstInSession,
    }
    this.track(eventName, properties, { eventID })
    if ((serverSideAnalyticsPlatforms || []).length && isFirstInSession) {
      this.reportServerAnalytics(
        eventName,
        properties,
        eventID,
        serverSideAnalyticsPlatforms
      )
      this._trackOncePerSessionEvents.add(eventName)
    }
  }

  trackFirstInteraction() {
    this.trackOncePerSession(
      'First Interaction',
      { 'Full URL': window.location.href },
      { serverSideAnalyticsPlatforms: [ServerSideAnalyticsPlatform.Facebook] }
    )
  }

  trackEmailCaptureTermsOfUseTapped() {
    this.track('Email Capture Terms Of Use Tapped')
  }

  trackEmailCapturePrivacyPolicyTapped() {
    this.track('Email Capture Privacy Policy Tapped')
  }

  trackFirstOpen() {
    const promoCode = getCleanUrlParam('promo')
    this.trackOnce('First Open', promoCode ? { promoCode } : {})
  }

  trackOncePerSession(event, eventPropertries = {}, options = {}) {
    if (!this._trackOncePerSessionEvents.has(event)) {
      this.track(event, eventPropertries, options)
      this._trackOncePerSessionEvents.add(event)
    }
  }

  wasTracked(eventName) {
    return this.alreadyTracked && this.alreadyTracked.has(eventName)
  }

  wasTrackedPerId(eventName, id) {
    return (
      this.alreadyTrackedPerId &&
      this.alreadyTrackedPerId.has(`${eventName}_${id}`)
    )
  }

  initAlreadyTracked() {
    if (storageManager.get(LOCAL_STORAGE_KEY) == null) {
      storageManager.set(LOCAL_STORAGE_KEY, { alreadyTracked: [] })
    }
    const { alreadyTracked, alreadyTrackedPerId } =
      storageManager.get(LOCAL_STORAGE_KEY) || {}
    this.alreadyTracked = new Set(alreadyTracked || [])
    this.alreadyTrackedPerId = new Set(alreadyTrackedPerId || [])
  }

  setAsTracked(eventName) {
    this.alreadyTracked.add(eventName)
    this.syncStorage()
  }

  setAsTrackedPerId(eventName, id) {
    this.alreadyTrackedPerId.add(`${eventName}_${id}`)
    this.syncStorage()
  }
}

export const _analytics = new Analytics()
